import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import type { EmailModalId } from 'config/emailModalDefaults.constants'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { findCloseParty, isRolodexPartyViewKey, partiesMapWithAccountTypes } from 'reducers/loanDetail.reducer'
import { getBorrowerInfo } from 'services'
import { borrowerEmailParties } from 'utils/borrowerRecipients'

import { mergeEmailValueWithPartyDefaults, normalizePartyDefault } from './applyEmailModalDefaults'
import { EmailParty, EmailTo, EmailValue } from './EmailTo'

export const LoanPartiesEmailTo = ({
  loanNumber = 0,
  value,
  data: _data,
  loadParties = false,
  includeGlobalParties = true,
  hasAddEmail = true,
  hasUniqueToEmail = false,
  onChange,
  addonContacts = [],
  modalId,
}: {
  loanNumber?: number
  value: EmailValue
  data: EmailParty[]
  loadParties?: boolean
  includeGlobalParties?: boolean
  hasAddEmail?: boolean
  hasUniqueToEmail?: boolean
  onChange: (data: EmailValue) => void
  addonContacts?: EmailParty[]
  /** When set, To/Cc/Bcc pre-checks load from admin Email → Default recipients (by party label). */
  modalId?: EmailModalId
}) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<EmailParty[]>(_data)
  const [isLoaded, setLoaded] = useState(false)

  const { auth, loanDetail, globalSettingV2, emailModalDefaults } = useSelector((state: any) => {
    return {
      auth: state.auth,
      loanDetail: state.loanDetail,
      globalSettingV2: state.globalConfigReducer[GlobalConfigType.GlobalSettingV2],
      emailModalDefaults: state.globalConfigReducer[GlobalConfigType.EmailModalDefaults] || {},
    }
  })

  const defaultsAppliedRef = useRef(false)
  const valueRef = useRef(value)
  useLayoutEffect(() => {
    valueRef.current = value
  }, [value])

  useEffect(() => {
    defaultsAppliedRef.current = false
  }, [loanNumber, modalId])
  const accountType = auth.profile.isBorrower ? 'borrower' : auth.profile.accountType
  const { parties } = loanDetail
  useEffect(() => {
    setLoaded(false)
  }, [loanNumber])

  useEffect(() => {
    if (isLoaded || loading) return
    if (!loadParties) {
      setData(_data)
      return
    }

    setLoading(true)
    getBorrowerInfo(loanNumber)
      .then(async ({ parties, borrowerEmail, borrowerAdditionalEmails, coBorrowerEmail, emailContacts }) => {
        const emails: EmailParty[] = cloneDeep(_data)
        // One selectable row per address the borrower keeps, not just the primary. The endpoint has
        // returned `borrowerAdditionalEmails` since the borrower record gained the column, but this
        // picker read only `borrowerEmail`, so the additional addresses a borrower asked to be
        // copied on were not even offered in any of the dialogs built on it.
        emails.push(...borrowerEmailParties({ email: borrowerEmail, additionalEmails: borrowerAdditionalEmails }))
        if (coBorrowerEmail)
          emails.push({
            party: 'CoBorrower',
            email: coBorrowerEmail,
          })
        const partiesMap = partiesMapWithAccountTypes()
        Object.keys(parties).forEach((key) => {
          // if (key == 'accountManager') return
          let email = parties[key]
          if (!email) return

          emails.push({
            party: partiesMap[key],
            email,
          })
        })
        emails.push(
          ...emailContacts.map((email: string) => ({
            party: 'E-Mail Notification Contacts',
            email,
          })),
        )

        if (includeGlobalParties) {
          ;(globalSettingV2.partyView?.[accountType] || [])
            .filter((key: string) => isRolodexPartyViewKey(key, partiesMap))
            .forEach((key: string) => {
              const closeParty = findCloseParty(loanDetail.closeParties, key)?.party
              if (!closeParty || !closeParty.EMail) return
              emails.push({
                party: closeParty.CustomName || closeParty.Type,
                email: closeParty.EMail,
              })
            })
        }
        const defaultEmails = emails.map((email) => email.email)
        addonContacts = addonContacts
          .filter((contact, index, self) => index === self.findIndex((t) => t.email === contact.email))
          .filter((contact) => !defaultEmails.includes(contact.email))

        emails.push(...addonContacts)
        setData(emails)
      })
      .finally(() => {
        setLoading(false)
        setLoaded(true)
      })
  }, [loadParties, _data, parties])

  const partyDefaults = modalId ? emailModalDefaults[modalId] : undefined

  const visibleData = useMemo(() => {
    if (!partyDefaults) return data
    return data.filter((row) => !normalizePartyDefault(partyDefaults[row.party]).hide)
  }, [data, partyDefaults])

  const partiesReady = loadParties ? isLoaded : true

  useEffect(() => {
    if (!modalId || loading || !partiesReady || defaultsAppliedRef.current) return
    if (!visibleData.length) return

    const merged = mergeEmailValueWithPartyDefaults(visibleData, partyDefaults, valueRef.current, hasUniqueToEmail)
    defaultsAppliedRef.current = true
    if (merged) onChange(merged)
  }, [modalId, loading, partiesReady, visibleData, partyDefaults, emailModalDefaults, hasUniqueToEmail, onChange])

  return (
    <div>
      <LayoutLoading show={loading} />
      <div className="mb-5"></div>
      <EmailTo
        Emails={value}
        Data={visibleData}
        onChange={(data: EmailValue) => onChange(data)}
        hasAddEmail={hasAddEmail}
        hasUniqueToEmail={hasUniqueToEmail}
      />
    </div>
  )
}
